<script lang="ts">
	import { browser } from '$app/env';
	import type { CollectionItem } from '$lib/api/velatlas/response.types';
	import { createLazyLoadAttachment } from '$lib/attachments/lazyLoad';
	import CollectionItemCard from '$lib/components/ui/collection/CollectionItemCard.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import type { MapPoint } from '$lib/components/ui/map/_other/mapPoint.type';
	import GenericMap from '$lib/components/ui/map/GenericMap.svelte';
	import type { ViewState } from '$lib/controllers/lastView/viewState';
	import type { LabelSlug } from '$lib/features/_other/labelSlug';
	import type { CollectionMetadata } from '$lib/features/collection/model/collectionMetadata';
	import { FULL_SITE_URL } from '$lib/statics';
	import { truncateDescription } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Snapshot } from '@sveltejs/kit';
	import { watch } from 'runed';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import type { Output } from './+page.server';

	const { data }: { data: { output: Output } } = $props();

	let loadMap = $state<boolean>(false);
	let mapViewState = $state<ViewState | undefined>(undefined);
	let map = $state<GenericMap | undefined>(undefined);

	const country = $derived<LabelSlug>(data.output.country);
	const metadata = $derived<CollectionMetadata>(data.output.collection.metadata);
	const items = $derived<CollectionItem[]>(data.output.collection.items);
	const mapPoints = $derived<MapPoint[]>(
		items.map((item) => ({
			id: item.id,
			label: item.englishName,
			type: item.type,
			coords: item.coordinates,
			href: UrlUtil.toCityFeature({
				countrySlug: item.country.slug,
				citySlug: item.city.slug,
				type: item.type,
				featureSlug: item.slug
			}).href,
			imageUrl: UrlUtil.getFullImageUrl(item.image),
			badges: [`${item.country.label}, ${item.city.label}`]
		}))
	);
	const meta = $derived.by(() => {
		const exampleNames: string[] = items.slice(0, 2).map((item) => item.englishName);
		const showCount = items.length > 12;

		const title = showCount
			? `${metadata.collectionTitle} in ${country.label}: ${items.length} to Explore`
			: `${metadata.collectionTitle} in ${country.label}`;

		const description = truncateDescription(
			`See ${exampleNames.join(', ')}, and more, plotted on an interactive map. ${metadata.description}`,
			160
		);
		return {
			title,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCountryCollection({ countrySlug: country.slug, collectionSlug: metadata.slug }).href}`,
			imageUrl: UrlUtil.getFullImageUrl(items[0].image)
		};
	});
	const jsonLdCollectionPage = $derived({
		'@context': 'https://schema.org' as const,
		'@type': 'CollectionPage' as const,
		name: meta.title,
		description: meta.description,
		url: meta.canonicalUrl
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdCollectionPage]);

	const observeMapLoading = createLazyLoadAttachment(() => (loadMap = true), '300px');

	watch([() => mapViewState, () => map], ([mapViewState, map], [prevMapViewState, prevMap]) => {
		const firstMapLoad = prevMap === undefined && map !== undefined;
		const shouldRepositionMap = firstMapLoad && mapViewState !== undefined;
		if (shouldRepositionMap) {
			map.jumpTo(mapViewState);
		}
	});

	export const snapshot: Snapshot<{
		mapViewState: ViewState | undefined;
	}> = {
		capture: () => {
			console.debug('Capturing state');
			return { mapViewState };
		},
		restore: (value) => {
			console.debug('Restoring state', value);
			mapViewState = value.mapViewState;
		}
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`${metadata.collectionTitle} in ${country.label}`}
	description={metadata.description}
	breadcrumbs={data.output.breadcrumbs}
	extraInfos={[`${items.length} place${items.length === 1 ? '' : 's'} found`]}
	decorations={{ coordinates: data.output.collection.items.flatMap((item) => item.coordinates).slice(0, 13) }}
/>

<div class="container mx-auto flex flex-col gap-12 px-4 py-14 md:py-16 lg:px-0">
	<section>
		<header class="flex justify-between px-2">
			<h2 class="text-2xl">{metadata.collectionTitle} On A Map</h2>
			<span class="hidden text-sm font-medium text-base-content/70 sm:block"> Click a pin to explore </span>
		</header>
		<div class="divider"></div>
		{@render MapContainer()}
	</section>

	<section id="results">
		<header class="flex flex-col gap-4 px-2 sm:flex-row sm:items-center sm:justify-between">
			<div class="flex items-baseline gap-2">
				<h2 class="text-2xl">Results</h2>
			</div>
		</header>

		<div class="divider"></div>

		<ul class="grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
			{#each items as item, i (item.id)}
				<li>
					<CollectionItemCard
						{item}
						loading={i < 3 ? 'eager' : 'lazy'}
						fetchPriority={i < 3 ? 'high' : 'auto'}
						withCountryName={false}
					/>
				</li>
			{/each}
		</ul>
	</section>

	<div class="group flex justify-center border-t border-base-content/10 pt-8">
		<a
			href="/{UrlUtil.toAllCollection({ slug: metadata.slug }).href}"
			class="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-primary hover:text-primary/80"
		>
			see {metadata.collectionTitle.toLowerCase()} from every country
			<IconArrowRight class="size-4 transition-transform duration-200 group-hover:translate-x-1" />
		</a>
	</div>
</div>

{#snippet MapContainer()}
	<figure aria-label="Map of {metadata.collectionTitle.toLowerCase()} from all over the world">
		<div {@attach observeMapLoading} class="h-[60vh] w-full overflow-hidden rounded-box shadow-sm">
			{#if browser && loadMap}
				{#await import('$lib/components/ui/map/GenericMap.svelte') then { default: GenericMap }}
					<GenericMap bind:this={map} points={mapPoints} lockToArea={false} bind:viewState={mapViewState} scrollZoom={true} />
				{/await}
			{:else}
				<div class="flex h-full w-full items-center justify-center bg-base-200 text-base-content/70">
					<span class="loading mr-2 loading-spinner text-primary"></span>
					<span>Loading map of {items.length} places...</span>
				</div>
			{/if}
		</div>
		<figcaption class="sr-only">
			Interactive map showing locations of {metadata.collectionTitle.toLowerCase()}, including
			{new Intl.ListFormat('en-GB', { style: 'short', type: 'conjunction' }).format(items.slice(0, 5).map((c) => c.englishName))}.
		</figcaption>
	</figure>
{/snippet}
